import { createSlice } from '@reduxjs/toolkit';

interface Identity {
  holder_name: string;
  number: string;
  address: string;
  phone: string;
}

interface Document {
  type: string;
  file: File;
  identity?: Identity;
  data?: object;
}

interface Channel {
  id: number;
  code: string;
  name: string;
  checked: boolean;
  icon: string;
}

export interface EventsInterface {
  slug?: string | null;
  code?: string | null;
  banner?: {
    fallback_url?: string | null;
    srcset?: string | null;
  };
  title?: string | null;
  description?: string | null;
  terms?: string | null;
  type?: string | null;
  status?: {
    color: string | null;
    label: string | null;
  };
  start_at?: string | null;
  end_at?: string | null;
  tickets: any;
  channels: Channel[];
  customer_data: any;
  documents: Document[];
  location?: {
    name?: string | null;
    url?: string | null;
    type?: string | null;
  };
}

const initialEvents: EventsInterface = {
  slug: null,
  code: null,
  banner: {
    fallback_url: null,
    srcset: null,
  },
  title: null,
  description: null,
  terms: null,
  type: null,
  status: {
    color: null,
    label: null,
  },
  start_at: null,
  tickets: [],
  channels: [],
  customer_data: [],
  documents: [],
  location: {
    name: null,
    url: null,
    type: null,
  },
};

interface initialCustomModalFormInterface {
  id: number;
  index: string;
  modal: boolean;
  label: string;
  type: string;
  media: File | undefined;
  media_url: string | undefined;
  possible_values: [];
}

const initialCustomModalForm: initialCustomModalFormInterface = {
  id: 8,
  index: '',
  modal: false,
  label: '',
  type: '',
  media: undefined,
  media_url: '',
  possible_values: [],
};

interface CustomModalFormInterface {
  id: number;
  customer_field_id: number;
  label: string;
  name: string;
  type: string;
  content: string | null;
  possible_values: string[];
  media: File;
  checked: boolean;
  mandatory: boolean;
}

const initialState = {
  events: [],
  tickets: [],
  studios: [],
  channels: [],
  committees: [],
  permits: [],
  socialMedias: [],
  socialMedia: {},
  committee: {},
  paginator: [],
  customer_data: [] as CustomModalFormInterface[],
  documents: [
    {
      type: 'identity',
      identity: {
        holder_name: '',
        number: '',
        address: '',
        phone: '',
      },
    },
    {
      type: 'permit',
    },
  ],
  event: initialEvents,
  ticket: {},
  loading: false,
  onPublishClicked: '',
  onEventCreated: '',
  onSuccessSubmit: false,
  onEventDeleted: false,
  onEventUpdated: false,
  errorMsg: '',
  errors: {},
  customFormModal: initialCustomModalForm,
  defaultCustomFormModal: initialCustomModalForm,
};

const eventSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    events: (state, action) => {
      state.events = action.payload;
    },
    setTickets: (state, action) => {
      state.tickets = action.payload;
    },
    setStudios: (state, action) => {
      state.studios = action.payload;
    },
    setChannels: (state, action) => {
      state.channels = action.payload;
    },
    setCustomerData: (state, action) => {
      state.customer_data = action.payload;
    },
    setTicketScanned: (state, action) => {
      state.ticket = action.payload;
    },

    updateChannelChecked: (
      state: any,
      action: { payload: { id: number; checked: boolean } }
    ) => {
      const { id, checked } = action.payload;
      state.channels = state.channels.map((channel: any) =>
        channel.id === id ? { ...channel, checked } : channel
      );
    },

    updateCustomerDataChecked: (
      state: any,
      action: { payload: { index: string; newCustomerData: any } }
    ) => {
      const { index, newCustomerData } = action.payload;
      const exists = state.customer_data.some(
        (_: any, customerDataIndex: string) => customerDataIndex === index
      );

      if (exists) {
        state.customer_data = state.customer_data.map(
          (customerData: any, customerDataIndex: string) =>
            customerDataIndex === index
              ? { ...customerData, ...newCustomerData }
              : customerData
        );
      } else {
        // Add new entry
        state.customer_data.push({ id: index, ...newCustomerData });
      }
    },

    updateDocument: (state: any, action: { payload: any }) => {
      const { type } = action.payload;

      state.documents = state.documents.filter((doc: any) => doc.type !== type);
      state.documents.push(action.payload);
    },

    setDocument: (state: any, action: { payload: any }) => {
      state.documents = action.payload;
    },

    setCustomFormModal: (state: any, action: { payload: any }) => {
      state.customFormModal = action.payload;
    },

    updateOrAddTicket: (state: any, action: { payload: any }) => {
      const updatedTicket = action.payload;

      const existingTicketIndex = state.tickets.findIndex(
        (ticket: any) => ticket.id === updatedTicket.id
      );

      if (existingTicketIndex !== -1) {
        state.tickets[existingTicketIndex] = {
          ...state.tickets[existingTicketIndex],
          ...updatedTicket,
        };
      } else {
        const existingIds = state.tickets.map((ticket: any) => ticket.id);
        state.tickets.push({
          ...updatedTicket,
          id: generateRandomId(existingIds),
        });
      }
    },

    updateOrAddStudio: (state: any, action: { payload: any }) => {
      const updatedStudio = action.payload;

      const existingTicketIndex = state.studios.findIndex(
        (ticket: any) => ticket.id === updatedStudio.id
      );

      if (existingTicketIndex !== -1) {
        state.studios[existingTicketIndex] = {
          ...state.studios[existingTicketIndex],
          ...updatedStudio,
        };
      } else {
        const existingIds = state.studios.map((ticket: any) => ticket.id);
        state.studios.push({
          ...updatedStudio,
          id: generateRandomId(existingIds),
        });
      }
    },

    deleteTicket: (state: any, action: { payload: any }) => {
      state.tickets = state.tickets.filter(
        (ticket: any) => ticket.id !== action.payload
      );
    },

    deleteStudio: (state: any, action: { payload: any }) => {
      state.studios = state.studios.filter(
        (ticket: any) => ticket.id !== action.payload
      );
    },

    setCommittees: (state, action) => {
      state.committees = action.payload;
    },

    setPermits: (state, action) => {
      state.permits = action.payload;
    },

    setSocialMedias: (state, action) => {
      state.socialMedias = action.payload;
    },

    setSocialMedia: (state, action) => {
      state.socialMedia = action.payload;
    },

    setCommittee: (state, action) => {
      state.committee = action.payload;
    },

    setEvent: (state, action) => {
      state.event = action.payload;
    },
    setPaginator: (state, action) => {
      state.paginator = action.payload;
    },
    getErrors: (state, action) => {
      state.errorMsg = action.payload.errorMsg;
      state.errors = action.payload.errors;
    },
    clearErrors: (state) => {
      state.errorMsg = '';
      state.errors = '';
    },
    loading: (state, action) => {
      state.loading = action.payload;
    },
    setOnPublishClicked: (state, action) => {
      state.onPublishClicked = action.payload;
    },
    setOnEventUpdated: (state, action) => {
      state.onEventUpdated = action.payload;
    },
    setOnEventCreated: (state, action) => {
      state.onEventCreated = action.payload;
    },
    setOnEventDeleted: (state) => {
      state.onEventDeleted = true;
    },
    setOnSuccessSubmit: (state, action) => {
      state.onSuccessSubmit = action.payload;
    },
  },
});

export const {
  loading,
  events,
  setEvent,
  setTicketScanned,
  setCustomFormModal,
  setStudios,
  setCommittees,
  setCommittee,
  setPermits,
  setSocialMedias,
  setSocialMedia,
  setOnEventCreated,
  setOnEventDeleted,
  setPaginator,
  updateDocument,
  setDocument,
  updateChannelChecked,
  updateCustomerDataChecked,
  updateOrAddTicket,
  updateOrAddStudio,
  deleteTicket,
  deleteStudio,
  clearErrors,
  setOnPublishClicked,
  setTickets,
  setChannels,
  setCustomerData,
  setOnSuccessSubmit,
  setOnEventUpdated,
  getErrors,
} = eventSlice.actions;
export default eventSlice.reducer;

const generateRandomId = (existingIds: string[]) => {
  let id;
  do {
    id = Math.floor(Math.random() * 100000)
      .toString()
      .padStart(8, '0');
  } while (existingIds.includes(id));
  return id;
};
